import Tag from "components/design/Tag";
import { Button } from "components/DesignSystem/Button/Button";
import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import Loader from "components/DesignSystem/Loader/loader";
import Modal from "components/DesignSystem/Modal/Modal";
import { Form, Formik, FormikValues } from "formik";
import { useToast } from "hooks/useToast";
import { MultiValue, SingleValue } from "react-select";
import {
  useGetTaskTagsQuery,
  useUpdateBulkTaskMutation,
} from "store/apis/task";
import { BackendError } from "types/utils/error";
import { array, object, string } from "yup";

type TaskBulkTagAssignProps = {
  isOpen: boolean;
  close: () => void;
  selectedRows: string[];
  resetSelection: () => void;
};

export const TaskBulkTagAssign = ({
  isOpen,
  close,
  selectedRows,
  resetSelection,
}: TaskBulkTagAssignProps) => {
  const [bulkTaskUpdate, { isLoading: isUpdating }] =
    useUpdateBulkTaskMutation();
  const {
    alertToast,
    successToast,
    loading: loadingToast,
    dismiss: dismissToast,
  } = useToast();
  const { data: tags = [], isLoading } = useGetTaskTagsQuery();

  const handleUpdate = async (values: FormikValues) => {
    const loadingtoastId = loadingToast({
      title: "Updating",
      message: "Updating tags...",
    });
    try {
      await bulkTaskUpdate({
        payload: {
          task_ids: selectedRows,
          tags: values.value,
        },
      }).unwrap();
      close();
      successToast({
        title: "Preparer updated",
        message: `Preparer updated for ${selectedRows.length} tasks`,
      });
      resetSelection();
    } catch (error) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
    } finally {
      dismissToast(loadingtoastId);
    }
  };

  return (
    <>
      <Modal.Root open={isOpen} onOpenChange={close}>
        <Modal.Content>
          <Modal.Header>
            <Modal.Title>Update Tags</Modal.Title>
          </Modal.Header>
          <Formik
            initialValues={{ value: [] as string[] }}
            onSubmit={handleUpdate}
            enableReinitialize
            validationSchema={object({
              value: array().of(string()).min(1, "Please select a value"),
            })}
          >
            {({ handleSubmit, setFieldValue, values }) => {
              const onTagsChange = (
                selectedTags: MultiValue<OptionData> | SingleValue<OptionData>
              ) => {
                setFieldValue(
                  "value",
                  (selectedTags as MultiValue<OptionData>).map(
                    (tag) => tag.value
                  )
                );
              };

              const selectedTags = tags.filter((tag) =>
                values.value.includes(tag.uuid)
              );
              return (
                <Form onSubmit={handleSubmit} className="t-m-0">
                  <Modal.Body>
                    {isLoading ? (
                      <Loader size="small" />
                    ) : (
                      <Combobox
                        menuPortalTarget={document.body}
                        isMulti
                        styles={{
                          menu: () => ({ width: "400px" }),
                        }}
                        name="value"
                        label="Select tags"
                        options={tags?.map(
                          (t: { title: string; uuid: string }) => ({
                            label: t.title,
                            value: t.uuid,
                          })
                        )}
                        value={selectedTags?.map(
                          (tag: {
                            uuid: string;
                            title: string;
                            bg_color_hex: string;
                            color_hex: string;
                          }) => ({
                            value: tag.uuid,
                            label: (
                              <Tag
                                size="small"
                                key={tag.uuid}
                                title={tag.title}
                                bg_color_hex={tag.bg_color_hex}
                                color_hex={tag.color_hex}
                              />
                            ),
                          })
                        )}
                        onChange={(
                          selectedTags:
                            | SingleValue<OptionData>
                            | MultiValue<OptionData>
                        ) => {
                          if (selectedTags && selectedTags instanceof Array) {
                            onTagsChange?.(
                              (selectedTags as {
                                label: string;
                                value: string;
                              }[]) || []
                            );
                          }
                        }}
                        isLoading={isLoading}
                        backspaceRemovesValue={false}
                      />
                    )}
                  </Modal.Body>

                  <Modal.FooterButtonGroup>
                    <Modal.RawClose asChild>
                      <Button type="button">Cancel</Button>
                    </Modal.RawClose>
                    <Button
                      customType="primary"
                      type="submit"
                      isLoading={isUpdating}
                    >
                      Update all ({selectedRows.length})
                    </Button>
                  </Modal.FooterButtonGroup>
                </Form>
              );
            }}
          </Formik>
        </Modal.Content>
      </Modal.Root>
    </>
  );
};
